import * as React from 'react';
import { computed } from 'mobx';
import { observer } from 'mobx-react';
import CSSTransition from 'react-transition-group/CSSTransition';
import TransitionGroup from 'react-transition-group/TransitionGroup';

import { FormField } from '../form-field';
import { FormControlLabel } from '../../primitives/label';
import { Textbox } from '../../primitives/textbox';
import { CardBrandConfig } from '../../webgiving-generated';
import { TextMask } from '../../utils/text-mask';
import { SvgIcon, SvgProperties } from '../svg-icon';
import { TextMaskInput } from '../text-mask-input';
import { FieldValidationMessage } from '../form-control/form-control-validation-error';
import { getCardBrandByInput, getIconScale } from '../../utils/card-utils';
import { FocusOnMount } from '../focus-on-mount-hoc';
import { panelContentTransitionDuration } from '../panel-content-transition';

import cardBrandIcons from '../card-brand-icons';
import cardIcon from '../../assets/svg/card.svg';
import * as styles from './card-number-input.less';
import { IParams } from '../../components/text';

const defaultMask = [/[0-9]/, /[0-9]/, /[0-9]/, /[0-9]/, ' ', /[0-9]/, /[0-9]/, /[0-9]/, /[0-9]/, ' ', /[0-9]/, /[0-9]/, /[0-9]/, /[0-9]/, ' ', /[0-9]/, /[0-9]/, /[0-9]/, /[0-9]/];
const minimalBrands = ['visa', 'mastercard'];

export interface ICardNumberInputProps {
	value: FormField<string>;
	formControlId: string;
	acceptanceTestTargetId: string;
	translationParams?: IParams;
}

@observer
export class CardNumberInput extends React.Component<ICardNumberInputProps> {
	render() {
		const { value, translationParams, formControlId, acceptanceTestTargetId } = this.props;
		const brandIcon = getBrandIconInfo(this.cardBrand);
		const additionalClassNames = value.hasValidationError ? 'form-control-invalid' : '';
		const iconScale = this.cardBrand && minimalBrands.includes(this.cardBrand.friendlyName.toLocaleLowerCase())  ? getIconScale(this.cardBrand.shortName, true) : undefined;
		
		return (
			<div className="form-control-container">
				<FormControlLabel formControlId={formControlId}>Card number</FormControlLabel>
				<div className={styles.inputWrapper}>
					<CardNumberIcon svgProps={brandIcon} scale={iconScale} />
					<FocusOnMount delay={panelContentTransitionDuration}>
						<TextMaskInput mask={this.getTextMask}>
							<Textbox
								value={value.value}
								formControlId={formControlId}
								placeholder="0000 0000 0000 0000"
								disableTranslation={true}
								onChange={value.updateValue}
								onBlur={value.revalidate}
								numericKeyboard
								acceptanceTestTargetId={acceptanceTestTargetId}
								dataFieldInvalid={value.hasValidationError}
								additionalClassNames={additionalClassNames}
							/>
						</TextMaskInput>
					</FocusOnMount>
				</div>
				<FieldValidationMessage translationParams={translationParams} errorMessage={value.validationError} />
			</div>
		);
	}

	@computed
	private get cardBrand() {
		return getCardBrandByInput(this.props.value.value);
	}

	private getTextMask = (currentValue: string): TextMask[] => {
		const cardBrand = getCardBrandByInput(currentValue);

		if (!cardBrand) {
			return defaultMask;
		} else {
			return cardBrand.cardNumberMask;
		}
	}
}

function getBrandIconInfo(cardBrand: CardBrandConfig | null): SvgProperties {
	let svg: SvgProperties = cardIcon;

	if (cardBrand) {
		const [iconKey] = cardBrandIcons.keys().filter(x => x.toLowerCase().indexOf(`${'paymentmethod-brand-'}${cardBrand.shortName.toLowerCase()}.svg`) !== -1);

		if (iconKey) {
			svg = (cardBrandIcons(iconKey) as any).default as SvgProperties;
		}
	}

	return svg;
}

interface ICardNumberIconProps {
	svgProps: SvgProperties;
	scale?: number;
}

const brandIconTransitionClassNames = {
	enter: styles.brandIconEnter,
	enterActive: styles.brandIconEnterActive,
};

const brandIconTransitionTimeout = parseInt(styles.brandIconTransitionDuration);

class CardNumberIcon extends React.Component<ICardNumberIconProps> {
	render() {
		return (
			<TransitionGroup>
				<CSSTransition key={this.props.svgProps.id} in={true} appear={false} exit={false} classNames={brandIconTransitionClassNames} timeout={brandIconTransitionTimeout}>
					<span className={styles.brandIconWrapper}>
						<SvgIcon svg={this.props.svgProps} role="presentation" scale={this.props.scale} />
					</span>
				</CSSTransition>
			</TransitionGroup>
		);
	}
}
